import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { Heart, ArrowLeft } from "lucide-react";

export default function Terms() {
    return (
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16 leading-relaxed text-[#1F2624]">
            <SeoHead
                title="Memory Lane Care — Terms of Service"
                description="Terms of service for Memory Lane Care — the continuity platform for dementia caregivers. Acceptable use, account responsibilities, and limitations."
                canonicalPath="/terms"
            />
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6">
                <ArrowLeft size={14} strokeWidth={1.8} /> Back to Memory Lane
            </Link>
            <header className="border-b border-[#E2E8F0] pb-6 mb-8">
                <div className="flex items-center gap-2.5 text-teal-dark mb-3">
                    <Heart size={22} strokeWidth={1.6} />
                    <span className="text-sm uppercase tracking-wider font-semibold">Memory Lane Care Navigator</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight">Terms of Service</h1>
                <p className="mt-3 text-sm text-[#4C5551]">Last updated: February 2026</p>
            </header>

            {/* Hero callout — three things in plain, friendly language so
                caregivers feel informed, not warned away. */}
            <aside
                className="rounded-2xl border border-teal-soft bg-teal-soft/30 p-5 mb-8"
                data-testid="terms-key-disclaimers"
                role="note"
                aria-label="Beta heads-up"
            >
                <p className="text-sm font-semibold text-teal-dark uppercase tracking-wider">A friendly heads-up</p>
                <p className="text-[15px] text-[#1F2624] mt-2 leading-relaxed">
                    Memory Lane is currently in <strong>beta</strong> — we&apos;re building it with caregivers and improving it every week. A few things to know up front:
                </p>
                <ul className="text-[15px] text-[#1F2624] mt-2 space-y-1.5 leading-relaxed list-disc list-inside">
                    <li>It&apos;s an <strong>educational and organizational tool</strong>, not a substitute for clinical advice. Lane (the AI companion) is there to help you learn and prepare — your loved one&apos;s clinicians make the medical calls.</li>
                    <li>It&apos;s <strong>not yet HIPAA-compliant</strong>. We treat your data with care, but for records that legally need to be Protected Health Information (PHI), use your hospital or hospice patient portal.</li>
                    <li>It&apos;s <strong>not an emergency service</strong>. In a crisis, call <a href="tel:911" className="text-teal-dark underline">911</a> or text <a href="tel:988" className="text-teal-dark underline">988</a>.</li>
                </ul>
            </aside>

            <Section title="The short version">
                <p>
                    Memory Lane is a private beta. It is built to support family caregivers of people with dementia, not to replace
                    medical, legal, or psychological care. The AI companion (Lane) is informational only. Treat anything Lane says as a
                    starting point — never a diagnosis or a substitute for your loved one&apos;s doctor, attorney, or emergency services.
                </p>
                <p>
                    By creating an account, you confirm you are at least 18 years old and agree to use Memory Lane in good faith and in
                    accordance with these terms.
                </p>
            </Section>

            <Section title="1. What Memory Lane is — and isn&apos;t">
                <ul>
                    <li><strong>Is</strong>: an educational and organizational tool — a personalized roadmap, conversation drafter, behavior tracker, and resource navigator.</li>
                    <li><strong>Is not</strong>: a medical device, a clinical decision-support system, a telehealth provider, a legal service, an emergency service, or a substitute for the human relationships in your loved one&apos;s care.</li>
                    <li>If you or your loved one is in danger or a mental-health crisis, call <a href="tel:911" className="text-teal-dark underline">911</a> or text <a href="tel:988" className="text-teal-dark underline">988</a>.</li>
                </ul>
            </Section>

            <Section title="2. Memory Lane is not a HIPAA-covered service">
                <p>
                    <strong>Memory Lane is not a HIPAA-covered entity, and the information you enter here is not Protected Health Information (PHI) in the legal sense.</strong> The U.S. Health Insurance Portability and Accountability Act (HIPAA) applies to healthcare providers, health plans, and the &quot;business associates&quot; they hire — Memory Lane is none of those. We are a self-service caregiver tool that you choose to use independently of your loved one&apos;s healthcare team.
                </p>
                <p>
                    Practically, this means:
                </p>
                <ul>
                    <li>We do <em>not</em> sign Business Associate Agreements (BAAs) with hospitals, hospices, clinics, or insurance companies.</li>
                    <li>Information you enter — behavior logs, intake answers, drafted family notes, journal entries — is protected by our <Link to="/privacy" className="text-teal-dark underline">Privacy Policy</Link> and applicable U.S. privacy laws (state-level laws like CCPA, where they apply), but it is <em>not</em> protected by HIPAA.</li>
                    <li>If you want a record of your loved one&apos;s care that is legally treated as PHI, keep it inside the patient portal your hospital, clinic, or hospice provides — not in Memory Lane.</li>
                    <li>Do not paste your loved one&apos;s social security number, full insurance ID, full credit card information, or other sensitive identifiers into Memory Lane. We don&apos;t need them, and we don&apos;t want to store them.</li>
                </ul>
                <p>
                    We still treat your data with care: we encrypt it in transit and at rest, we don&apos;t sell it, we don&apos;t share it with advertisers, and we don&apos;t use it to train outside AI models. See the <Link to="/privacy" className="text-teal-dark underline">Privacy Policy</Link> for details.
                </p>
            </Section>

            <Section title="3. Beta access and changes">
                <p>
                    Memory Lane is in private beta. Features, copy, designs, and data structures may change frequently. We may add, remove, or revise functionality at any time without notice. Bugs are likely. By participating you agree to receive transparent communication from us about changes and to share honest feedback when asked.
                </p>
                <p>
                    We may suspend or terminate accounts that are abusive, automated, fraudulent, or violate these terms. We will try to give you reasonable notice and a chance to export data, except where doing so would be unsafe or illegal.
                </p>
            </Section>

            <Section title="4. Your account">
                <p>
                    You are responsible for the security of your password and for everything that happens under your account. If you suspect unauthorized access, contact us right away at <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>.
                </p>
                <p>
                    You agree to provide accurate information at signup. If you create accounts on behalf of others (e.g., another family member), you confirm you have their permission.
                </p>
            </Section>

            <Section title="5. Your content">
                <p>
                    Everything you write into Memory Lane — your intake answers, behavior logs, profile sheet, drafted messages, feedback — belongs to you. We store it to make Memory Lane work for you. You can request a copy or deletion at any time by emailing us.
                </p>
                <p>
                    By providing content, you grant us a limited license to use, store, and process it for the purpose of running Memory Lane for you. We will not sell your content, share it with advertisers, or use it to train external AI models without your explicit, opt-in consent.
                </p>
            </Section>

            <Section title="6. AI-generated content">
                <p>
                    Lane (our AI companion) generates text based on your inputs and a large language model. It can be wrong, biased, or out of date. It does not know your loved one personally. Always verify clinical, legal, financial, or safety-critical guidance with a qualified human professional before acting on it.
                </p>
                <p>
                    Drafts produced by Lane are intended as starting points. You are responsible for what you send to anyone — family members, clinicians, attorneys, or others.
                </p>
            </Section>

            <Section title="7. Acceptable use">
                <p>Don&apos;t use Memory Lane to:</p>
                <ul>
                    <li>Harass, threaten, or impersonate anyone, including your loved one or other family members.</li>
                    <li>Attempt to scrape, reverse-engineer, or stress-test the service.</li>
                    <li>Upload content that violates someone else&apos;s privacy, intellectual property, or dignity.</li>
                    <li>Use Memory Lane to make automated decisions about another person&apos;s healthcare, finances, or legal rights.</li>
                </ul>
            </Section>

            <Section title="8. Privacy, cookies, and analytics">
                <p>
                    Our <Link to="/privacy" className="text-teal-dark underline">Privacy Policy</Link> explains what we collect, why we collect it, who can see it, and how to delete it. Our <Link to="/cookies" className="text-teal-dark underline">Cookie Policy</Link> lists every cookie and analytics identifier we set — including Google Analytics 4 (anonymized), PostHog product analytics, and Microsoft Clarity (when enabled). We do not run ad-network pixels.
                </p>
            </Section>

            <Section title="9. Third-party services we use to run Memory Lane">
                <p>
                    Memory Lane depends on a small set of third-party services to do its job. Using
                    Memory Lane means you accept that data flows through them for the narrow purposes
                    listed:
                </p>
                <ul>
                    <li><strong>AI providers</strong> (OpenAI, Anthropic, Google — via our Emergent integrations key) power Lane&apos;s conversational guidance. They see your chat messages and a small context block built from your intake; they are contractually prohibited from training on this data.</li>
                    <li><strong>Stripe</strong> handles Care Navigator session billing. We never see your card details.</li>
                    <li><strong>Calendly</strong> handles scheduling for Care Navigator and partner intro calls.</li>
                    <li><strong>Resend</strong> sends transactional email (welcome, share links, notifications).</li>
                    <li><strong>Google Analytics 4, PostHog, and (when enabled) Microsoft Clarity</strong> give us aggregated usage analytics so we can improve the product.</li>
                </ul>
                <p>
                    The <Link to="/security" className="text-teal-dark underline">Security page</Link> lists every sub-processor and exactly what data each one receives.
                </p>
            </Section>

            <Section title="10. No warranties; limitation of liability">
                <p>
                    Memory Lane is provided <em>&quot;as is.&quot;</em> We make no warranties — express, implied, or statutory — about the accuracy, reliability, or fitness of the service for any particular purpose, including any medical, legal, or financial purpose.
                </p>
                <p>
                    To the maximum extent allowed by law, we are not liable for indirect, incidental, consequential, special, or exemplary damages, including loss of data, lost profits, or harm arising from reliance on AI-generated content. Our total liability is limited to the amount you have paid us in the previous 12 months (which, during beta, is zero).
                </p>
                <p>
                    Nothing in these terms limits liability for fraud, gross negligence, or any other liability that cannot be excluded by applicable law.
                </p>
            </Section>

            <Section title="11. Changes to these terms">
                <p>
                    We may update these terms as Memory Lane evolves. If we make a material change, we&apos;ll show a notice in the app and email approved users. Continued use after the change means you accept the updated terms.
                </p>
            </Section>

            <Section title="12. Contact">
                <p>
                    Questions, requests, concerns: <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>.
                </p>
            </Section>

            <p className="text-sm text-[#6E7A74] italic mt-12">
                These terms are written in plain language and are not a substitute for legal advice. We&apos;ll revise them as Memory Lane grows.
            </p>
        </article>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold tracking-tight mb-3">{title}</h2>
            <div className="space-y-3 text-base">{children}</div>
        </section>
    );
}
